import Form, { FormData } from "../../Components/Form/Form";
import { getRegionalMembershipId, getUserProfile } from "../../services/userService";
import { useUpdateUserProfile } from "../../services/userMutations";
import { AuthContext, AuthContextType, ConditionalRenderContext, ConditionalRenderContextType, LangContext, LangContextType, LoadingContext, LoadingContextType, ToastContext, ToastContextType} from "../../utilities/customHooks";
import * as T from "../../utilities/frontendTypes";

import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { ButtonProp } from "../../Components/Button";
import { useCreateFounderApplication, useEditedApplicationChange } from "../../services/founderMutations";
import { flattenObject, getModifiedCompanyInfoFormDef } from "../../utilities/helperFunctions";
import Modal from "../../Components/Modal";
import textData from "../../textData.json";
import FileUpload from  "../../Components/Form/pdfUpload"
import { needHelpWithDefinition, profileFormPersonalInfoDefinition, profileFormProgramingEvaluationDefinition } from "../../utilities/profileFormDefitions"
import { getNeedHelpFormDef } from "../../utilities/getNeedHelpFormDef"
import ProfileSideNav, { FormCompletenessState, FounderProfileSectionKey } from "./components/ProfileSideNav";
import CompanyInfoSection from "./components/CompanyInfoSection";
import NeedHelpWithSection from "./components/NeedHelpWithSection";
import { Checkbox } from "../../Components/ui/checkbox";
import { Label } from "../../Components/ui/label";
import { Card, CardContent } from "../../Components/ui/card";
import { CheckCircle2, Pencil, Upload } from "lucide-react";
import { Button as ShadcnButton } from "../../Components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/ui/tooltip";
import ApplicationSectionHeader from "./components/ApplicationSectionHeader";

function FounderProfileUpdated() {
  const { langState } = useContext(LangContext) as LangContextType
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;
  const { memberApplicationStatus, setMemberApplicationStatus } = useContext(AuthContext) as AuthContextType; 
  const navigate = useNavigate();
  const conditionalRenderContext = useContext(ConditionalRenderContext) as ConditionalRenderContextType;
  const [founderProfile, setFounderProfile] = useState<T.StartupFounderProfile | null>(null);
  const {showLoading, hideLoading} = useContext(LoadingContext) as LoadingContextType;
  const updateProfile = useUpdateUserProfile("startupFounder");
  const editedAppMutation = useEditedApplicationChange();
  const createAppMutation = useCreateFounderApplication();
  const [isAttestationChecked, setIsAttestationChecked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentHTML, setCurrentHTML] = useState<FounderProfileSectionKey>(
    memberApplicationStatus !== "unsubmitted" && memberApplicationStatus !== "pendingEditRequest" ? "review" : "personalInfo"
  )
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [pdfSaved, setPdfSaved] = useState(false);
  //FORM STATES
  const [personalInfo, setPersonalInfo] = useState<T.PersonalInfo | null>(null)
  const [companyInfo, setCompanyInfo] = useState<T.CompanyInfo | null>(null)
  const [needHelpWith, setNeedHelpWith] = useState<T.NeedHelpWith | null>(null)
  const [programingEvaluation, setProgramingEvaluation] = useState<T.ProgramingEvaluation | null>(null)
  const [pitchDeck, setPitchDeck] = useState(false)
  //FORM COMPLETENESS
  const [personalInfoComplete, setPersonalInfoComplete] = useState<FormCompletenessState>("incomplete")
  const [companyInfoComplete, setCompanyInfoComplete] = useState<FormCompletenessState>("incomplete")
  const [pitchDeckComplete, setPitchDeckComplete] = useState<FormCompletenessState>("incomplete")
  const [programingEvaluationComplete, setProgramingEvaluationComplete] = useState<FormCompletenessState>("incomplete")
  const [needHelpWithComplete, setNeedHelpWithComplete] = useState<FormCompletenessState>("incomplete")
  const [mailingAddress, setMailingAddress] = useState<any>(null)
  // Company-specific state moved to CompanyInfoSection
  // NeedHelp-specific state moved to NeedHelpWithSection
  const [regionalFirmOptions, setRegionalFirmOptions] = useState<{value: string, text: string, frText: string}[]>([])

  // FormCompletenessState type moved to components/ProfileSideNav.tsx

  // Option arrays moved to CompanyInfoSection and NeedHelpWithSection

  const handlePdfStateChange = () => {
    setPdfUploaded(true);
  };

  const personalFormModifier = () => {
      const formConts = document.getElementsByClassName('formComponentCont');
      if (formConts.length > 0) {
          formConts[0].classList.add('blockFormCont');
      }
      if (formConts.length > 1) {
          formConts[1].classList.add('blockFormCont');
      }
    }

  const htmlCheck = () => {
    if (personalInfoComplete === "incomplete") {
      setCurrentHTML("personalInfo")
    } else if (companyInfoComplete === "incomplete") {
      setCurrentHTML("companyInfo")
    } else if (pitchDeckComplete === "incomplete") {
      setCurrentHTML("pitchDeck")
    } else if (needHelpWithComplete === "incomplete") {
      setCurrentHTML("needHelpWith")
    } else if (programingEvaluationComplete === "incomplete") {
      setCurrentHTML("programingEvaluation")
    } else if (programingEvaluationComplete === "complete") {
      setCurrentHTML("review")
    } 
      else {
      setCurrentHTML("personalInfo")
    }
  }

  // Status circle SVGs moved to components/ProfileSideNav.tsx

    //WHEN READY TO SUBMIT
    const modalProfileCheck = () => {

      if (!founderProfile) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteProfile[langState]);
        return;
      }

      const {
        personalInfo,
        pointOfContact,
        companyInfo,
        needHelpWith,
        programingEvaluation,
      } = founderProfile;

      if (!companyInfo) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteCompany[langState]);
        return;
      }

      if(!pitchDeck) {
          handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitMissingPitchDeck[langState]);
          return;
      }

      const { registration,
              mailingAddress,
              basicInfo } = companyInfo;

      if (personalInfo === null) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompletePersonal[langState]);
        return;
      }
      if (!personalInfo.isPointOfContact && pointOfContact === null) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompletePoc[langState]);
        return;
      }
      if (basicInfo === null || registration === null || mailingAddress === null) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteCompany[langState]);
        return;
      }
      if (needHelpWith === null) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteNeedHelp[langState]);
        return;
      }
      if (programingEvaluation === null) {
        handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteProgramEval[langState]);
        return;
      }
      setIsModalOpen(true)
    }
  
  // FounderProfileSectionKey type moved to components/ProfileSideNav.tsx
  

useEffect(() => {
    const contentArea = document.getElementById("profile-content");
    if (contentArea) {
      contentArea.scrollTop = 0;
    }
  }, [currentHTML]);

  useEffect(() => {
    try {
      const fetchFounderProfile = async () => {
        showLoading();
        const founderProfileRes = await getUserProfile("startupFounder");
        hideLoading();
        // REMOVED: navigate("/dashboard") - was redirecting users away from application page
        const initialProfile = founderProfileRes.profile as T.StartupFounderProfile | null;
        // setCompanyName moved to CompanyInfoSection
        if (founderProfileRes.regionalFirmOptions) {
          setRegionalFirmOptions(founderProfileRes.regionalFirmOptions)
        }
        setMailingAddress(initialProfile?.companyInfo?.mailingAddress)
        setFounderProfile(initialProfile);
        //FORM STATES
        if (initialProfile?.personalInfo) {
          setPersonalInfo(initialProfile.personalInfo)
          setPersonalInfoComplete("complete")
        }
        // Company editing state initialization moved to CompanyInfoSection
        if (initialProfile?.companyInfo && initialProfile.companyInfo.basicInfo !== null && initialProfile.companyInfo.registration !== null && initialProfile.companyInfo.mailingAddress) {
          setCompanyInfo(initialProfile.companyInfo)
          const techOtherBoolean = initialProfile.companyInfo.basicInfo.tech && initialProfile.companyInfo.basicInfo.tech.includes("Other") && initialProfile.companyInfo.basicInfo.techOther || initialProfile.companyInfo.basicInfo.tech && !initialProfile.companyInfo.basicInfo.tech.includes("Other")
          const industryOtherBoolean = initialProfile.companyInfo.basicInfo.industry && initialProfile.companyInfo.basicInfo.industry.includes("Other") && initialProfile.companyInfo.basicInfo.industryOther || initialProfile.companyInfo.basicInfo.industry && !initialProfile.companyInfo.basicInfo.industry.includes("Other")
          const provinceBoolean = initialProfile.companyInfo.registration.registeredProvincially && initialProfile.companyInfo.registration.province.length > 0 || initialProfile.companyInfo.registration.registeredProvincially === false
          if (initialProfile.companyInfo.basicInfo.operatingName && initialProfile.companyInfo.basicInfo.legalName && initialProfile.companyInfo.basicInfo.description &&
            techOtherBoolean && industryOtherBoolean && initialProfile.companyInfo.basicInfo.websiteURL && initialProfile.companyInfo.basicInfo.techReadiness &&
            initialProfile.companyInfo.basicInfo.techReadiness && initialProfile.companyInfo.basicInfo.patentsGranted !== null && initialProfile.companyInfo.basicInfo.revFromTech !== null &&
            initialProfile.companyInfo.basicInfo.numFullTimeEmployeesCan !== null && initialProfile.companyInfo.basicInfo.numFullTimeEmployees !== null && initialProfile.companyInfo.basicInfo.revenueOverTwelveMonths !== null &&
            initialProfile.companyInfo.registration.date && initialProfile.companyInfo.registration.number && initialProfile.companyInfo.registration.registeredFederally !== null && provinceBoolean
          ) {
            setCompanyInfoComplete("complete")
          }
        }
        if (initialProfile?.documents?.pitchdeck) {
          setPitchDeck(true)
          setPitchDeckComplete("complete")
        }
        // NeedHelp editing state initialization moved to NeedHelpWithSection
        if (initialProfile?.needHelpWith) {
          setNeedHelpWith(initialProfile.needHelpWith)
          const specificDiscussionBoolean = initialProfile.needHelpWith.businessGoal === false || initialProfile.needHelpWith.businessGoal === true && initialProfile.needHelpWith.specificDiscussion
          const topicsBoolean = initialProfile.needHelpWith.businessGoal === false || initialProfile.needHelpWith.businessGoal === true && initialProfile.needHelpWith.topics
          const desiredActivitiesOtherBoolean = !initialProfile.needHelpWith.desiredActivities.includes("Other") || initialProfile.needHelpWith.desiredActivities.includes("Other") && initialProfile.needHelpWith.activitiesOther
          const searchCompletedBoolean = !initialProfile.needHelpWith.desiredActivities.includes("Trademark Filing") || initialProfile.needHelpWith.searchCompleted
          const workingWithFirmBoolean = initialProfile.needHelpWith.workingWithFirm === false || initialProfile.needHelpWith.workingWithFirm === true && initialProfile.needHelpWith.firmId
          const firmOtherBoolean = initialProfile.needHelpWith.workingWithFirm === false || initialProfile.needHelpWith.workingWithFirm === true && initialProfile.needHelpWith.firmId !== "Other" || initialProfile.needHelpWith.workingWithFirm === true && initialProfile.needHelpWith.firmId == "Other" && initialProfile.needHelpWith.firmOther
          const supportOrgBoolean = initialProfile.needHelpWith.govtSupport === false || initialProfile.needHelpWith.govtSupport === true && initialProfile.needHelpWith.supportOrg
          const supportOrgOtherBoolean = initialProfile.needHelpWith.govtSupport === false || initialProfile.needHelpWith.govtSupport === true && initialProfile.needHelpWith.supportOrg !== "Other" || initialProfile.needHelpWith.govtSupport === true && initialProfile.needHelpWith.supportOrg == "Other" && initialProfile.needHelpWith.supportOrgOther
          const exclusiveRightsBoolean = initialProfile.needHelpWith.desiredActivities.length === 0
            || initialProfile.needHelpWith.exclusiveRights === undefined
            || !!initialProfile.needHelpWith.exclusiveRights
          const agreementSignedBoolean = initialProfile.needHelpWith.agreementSigned != null
          if (initialProfile.needHelpWith.seekingEducation !== null && initialProfile.needHelpWith.businessGoal !== null
            && specificDiscussionBoolean && topicsBoolean && desiredActivitiesOtherBoolean && searchCompletedBoolean
            && initialProfile.needHelpWith.workingWithFirm !== null && workingWithFirmBoolean && firmOtherBoolean && initialProfile.needHelpWith.govtSupport !== null
            && supportOrgOtherBoolean && supportOrgBoolean && exclusiveRightsBoolean && agreementSignedBoolean
          ) {
            setNeedHelpWithComplete("complete")
          }
        }
        if (initialProfile?.programingEvaluation) {
          setProgramingEvaluation(initialProfile?.programingEvaluation)
          setProgramingEvaluationComplete("complete")
        }

        // Register conditional render state
        const conditionalRenderInitialState = {
          isPointOfContact: !!initialProfile?.personalInfo?.isPointOfContact,
          isLegalNameSameAsOperatingName: !!initialProfile?.companyInfo?.basicInfo?.isLegalNameSameAsOperatingName,
          isWorkingWithLawFirm: !!initialProfile?.iPStrategy?.isWorkingWithLawFirm,
          isWorkingWithBai: !!initialProfile?.programingEvaluation?.isWorkingWithBai,
          isFromOLMC: !!initialProfile?.programingEvaluation?.isFromOLMC,
          filePatentReady: !!initialProfile?.iPStrategy?.filePatentReady,
          fileTrademarkReady: !!initialProfile?.iPStrategy?.fileTrademarkReady,
          isProductForSaleOnMarket: !!initialProfile?.programingEvaluation?.isProductForSaleOnMarket,
        }
        conditionalRenderContext.replaceConditionalRenderState(conditionalRenderInitialState);
      }
      fetchFounderProfile().catch(console.error);
    } catch {
      // Error already handled by .catch above
    }
    // eslint-disable-next-line 
  }, [pdfSaved, programingEvaluationComplete]);

  useEffect(() => {
    if (founderProfile) htmlCheck()
  }, [founderProfile]);

  // logic to handle FormGroup validation on FounderProfile
  const handleSubmissionReject = (message?: string) => {
    showToast({
      toastType: "info",
      title: textData.Pages.FounderPages.FounderProfile.UnsuccessfulSubmit[langState],
      message: message || textData.Pages.FounderPages.FounderProfile.UnsuccessfulSubmitMessage[langState],
    })
  }

  const handleSubmitMembershipApplication = async () => {

    if (!founderProfile) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteProfile[langState]);
      return;
    }

    const {
      personalInfo,
      pointOfContact,
      companyInfo,
      programingEvaluation,
      needHelpWith,
    } = founderProfile;

    if (!companyInfo) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteCompany[langState]);
      return;
    }

    const { registration,
            mailingAddress,
            basicInfo } = companyInfo;

    if (personalInfo === null) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompletePersonal[langState]);
      return;
    }
    if (!personalInfo.isPointOfContact && pointOfContact === null) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompletePoc[langState]);
      return;
    }
    if (basicInfo === null || registration === null || mailingAddress === null) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteCompany[langState]);
      return;
    }
    if (needHelpWith === null) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteNeedHelp[langState]);
      return;
    }
    if (programingEvaluation === null) {
      handleSubmissionReject(textData.Pages.FounderPages.FounderProfile.SubmitIncompleteProgramEval[langState]);
      return;
    }

    const onAppSuccess = (response: T.FounderMembershipApplicationResponse) => {
      setIsModalOpen(false);
      setMemberApplicationStatus(response.applicationStatus);
      navigate("/dashboard");
    };

    // Create-path toasts live here (not as a hook successMessage) because an
    // autoReject response is also a "success" — a record was created, then
    // system-rejected — and must still move the founder off the submit form.
    // Leaving the form armed after autoReject caused duplicate records.
    const onCreateSuccess = (response: T.FounderMembershipApplicationResponse) => {
      if (response.autoReject) {
        showToast({
          toastType: "info",
          title: textData.Pages.FounderPages.FounderProfile.UnsuccessfulSubmit[langState],
          message: response.message,
        });
      } else {
        showToast({ toastType: "success", message: "Application submitted" });
      }
      onAppSuccess(response);
    };

    if (memberApplicationStatus === "pendingEditRequest") {
      editedAppMutation.mutate(undefined, { onSuccess: onAppSuccess });
    } else {
      const programId = (await getRegionalMembershipId()).membershipId;
      createAppMutation.mutate(programId, { onSuccess: onCreateSuccess });
    }
  }
  
  function handleSaveSection(updatedSectionFormData: FormData, sectionKey: string) {
    const emptyProfile = {
      personalInfo: null, 
      pointOfContact: null, 
      companyInfo: null,
      iPStrategy: null, 
      ipReadiness: null,
      pitchDeck: null,
      programingEvaluation: null,
      needHelpWith: null,
      sLASigningTimestamp: new Date(),
    }

    const updatedFounderProfile: T.StartupFounderProfile = founderProfile === null ? emptyProfile : {...founderProfile};
    switch (sectionKey as FounderProfileSectionKey) {
      case "personalInfo":
        const personal = updatedSectionFormData as T.PersonalInfo
        updatedFounderProfile.personalInfo = {
            firstName: personal.firstName,
            lastName: personal.lastName,
            pronouns: personal.pronouns,
            title: personal.title,
            phoneNumber: personal.phoneNumber,
            isPointOfContact: true
        }
        if (updatedFounderProfile.personalInfo.isPointOfContact) {
          updatedFounderProfile.pointOfContact = null;
        }
        break;
      case "companyInfo":
        // Data assembly handled by CompanyInfoSection — this case is reached via handleCompanyInfoSave
        break;
      case "needHelpWith":
        // Data assembly handled by NeedHelpWithSection — this case is reached via handleNeedHelpWithSave
        break;
      case "programingEvaluation":
        const underRepresentedGroupsArray = updatedSectionFormData.underrepresentedFounderGroups as string[];
        const isOwnedByUnderrepresentedGroups = !underRepresentedGroupsArray.includes("none");
        updatedSectionFormData.isOwnedByUnderrepresentedGroups = isOwnedByUnderrepresentedGroups
        updatedFounderProfile.programingEvaluation = updatedSectionFormData as T.ProgramingEvaluation;
        break;
      default:
        throw new Error(`Unknown sectionKey: ${sectionKey}`);
    }
    //MIGHT HAVE TO GO IN THE SUCCESS, SET THE STATE TO THE RETURN FROM STARTUPFOUNDER PROFILE
    updateProfile.mutate(updatedFounderProfile, {
      onSuccess: () => {
        setFounderProfile(updatedFounderProfile);
        switch (sectionKey as FounderProfileSectionKey) {
          case "personalInfo":
            setPersonalInfoComplete("complete")
            setCurrentHTML("companyInfo")
            break;
          case "companyInfo":
            setCompanyInfoComplete("complete")
            setCurrentHTML("needHelpWith")
            break;
          case "needHelpWith":
            setNeedHelpWithComplete("complete")
            setCurrentHTML("programingEvaluation")
            break;
          case "programingEvaluation":
            setProgramingEvaluationComplete("complete")
            if (personalInfoComplete === "complete"
              && companyInfoComplete === "complete"
              && pitchDeckComplete === "complete"
              && programingEvaluationComplete === "complete") {
                setCurrentHTML("review")
              } else (
                htmlCheck()
              )
            break;
          default:
            throw new Error(`Unknown sectionKey: ${sectionKey}`);
        }
      },
    });
  }

  const handleCompanyInfoSave = (companyInfo: T.CompanyInfo) => {
    const emptyProfile = {
      personalInfo: null,
      pointOfContact: null,
      companyInfo: null,
      iPStrategy: null,
      ipReadiness: null,
      pitchDeck: null,
      programingEvaluation: null,
      needHelpWith: null,
      sLASigningTimestamp: new Date(),
    }
    const updatedFounderProfile: T.StartupFounderProfile = founderProfile === null ? emptyProfile : {...founderProfile};
    updatedFounderProfile.companyInfo = companyInfo;
    updateProfile.mutate(updatedFounderProfile, {
      onSuccess: () => {
        setFounderProfile(updatedFounderProfile);
        setCompanyInfoComplete("complete")
        setCurrentHTML("needHelpWith")
      },
    });
  }

  const handleNeedHelpWithSave = (needHelpWith: T.NeedHelpWith) => {
    const emptyProfile = {
      personalInfo: null,
      pointOfContact: null,
      companyInfo: null,
      iPStrategy: null,
      ipReadiness: null,
      pitchDeck: null,
      programingEvaluation: null,
      needHelpWith: null,
      sLASigningTimestamp: new Date(),
    }
    const updatedFounderProfile: T.StartupFounderProfile = founderProfile === null ? emptyProfile : {...founderProfile};
    updatedFounderProfile.needHelpWith = needHelpWith;
    updateProfile.mutate(updatedFounderProfile, {
      onSuccess: () => {
        setFounderProfile(updatedFounderProfile);
        setNeedHelpWithComplete("complete")
        setCurrentHTML("programingEvaluation")
      },
    });
  }

  // needHelpWithErrorCheck moved to NeedHelpWithSection

  const attestationPrompt = () => {
    
    showToast({
      toastType: "failure",
      message: textData.Pages.FounderPages.FounderProfile.AttestationFailure[langState]
    })
  }

  const attestationTextEng = `I, ${founderProfile?.personalInfo?.firstName} ${founderProfile?.personalInfo?.lastName}, have legal authority to represent ${founderProfile?.companyInfo?.basicInfo?.operatingName} and attest that the support provided by Communitech / Invest Ottawa / North Forge under the ElevateIP program will not be used for any services that are already funded by another government program or grant. Any use of the ElevateIP program funds on services that are already funded by another government program or grant may lead to immediate termination from the ElevateIP program and/or the immediate return of such funding.`
  const attestationTextFr = `Je, ${founderProfile?.personalInfo?.firstName} ${founderProfile?.personalInfo?.lastName}, ai l'autorité légale de représenter ${founderProfile?.companyInfo?.basicInfo?.operatingName} et j'atteste que le soutien fourni par Communitech / Invest Ottawa / North Forge dans le cadre du programme ÉleverlaPI ne sera pas utilisé pour des services qui sont déjà financés par un autre programme gouvernemental ou une subvention.  Toute utilisation des fonds du programme ÉleverlaPI pour des services qui sont déjà financés par un autre programme gouvernemental ou une subvention pourrait entraîner l'annulation immédiate du programme ÉleverlaPI et/ou la demande de restitution immédiate de ces fonds.`


  const communitechAttestation = langState === "fr" ? attestationTextFr : attestationTextEng

  const modalHTML  = (
    <div className="flex flex-col gap-6 p-5">
        <h2 className="text-center">Attestation</h2>
        <p>{communitechAttestation}</p>
        <div className="flex items-center gap-3">
          <Checkbox
            id="attestation-checkbox"
            checked={isAttestationChecked}
            onCheckedChange={(checked) => setIsAttestationChecked(checked === true)}
          />
          <Label htmlFor="attestation-checkbox" className="cursor-pointer">
            {textData.Pages.FounderPages.FounderProfile.Confirm[langState]}
          </Label>
      </div>
    </div>
  )

  const pdfSave = () => {
    setPitchDeckComplete("complete")
    htmlCheck()
    setCurrentHTML("needHelpWith")
    setPdfSaved(true)
  }

  const saveSectionInfo = (updateForm: FormData) => {
    handleSaveSection(updateForm, currentHTML)
  }

  const saveSectionButtonProp: ButtonProp = {
    buttonText: textData.Pages.FounderPages.FounderProfile.Save[langState],
    actionHandler: saveSectionInfo
  }

  // Refs for sticky header save triggers
  const personalInfoSubmitRef = useRef<(() => void) | null>(null);
  const companyInfoSaveRef = useRef<(() => void) | null>(null);
  const needHelpWithSaveRef = useRef<(() => void) | null>(null);
  const programingEvalSubmitRef = useRef<(() => void) | null>(null);

  const companyInfoToForm = (info: T.CompanyInfo) => {
    return flattenObject({basicInfo: info.basicInfo, registration: info.registration})
  }

  // NeedHelp handlers, requiredFieldErrorMessage, option arrays moved to NeedHelpWithSection

  if (!founderProfile) return null;

  return (
    <div className="flex h-[calc(100vh-3.5rem)]" id="top">
        {currentHTML !== "review" && (
      <ProfileSideNav
        currentHTML={currentHTML}
        setCurrentHTML={setCurrentHTML}
        memberApplicationStatus={memberApplicationStatus}
        personalInfoComplete={personalInfoComplete}
        companyInfoComplete={companyInfoComplete}
        pitchDeckComplete={pitchDeckComplete}
        needHelpWithComplete={needHelpWithComplete}
        programingEvaluationComplete={programingEvaluationComplete}
        langState={langState}
      />
    )}
      <div id="profile-content" className={`flex flex-col items-center overflow-y-auto h-full w-full ${currentHTML !== "review" ? "menuOffset" : ""}`}>
          <div className="w-full flex flex-col px-24 pt-8 pb-8">
          {
            currentHTML === "personalInfo" &&
            <div className="flex-col flex">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.YourInfo[langState]}
                actions={<ShadcnButton onClick={() => personalInfoSubmitRef.current?.()}>{textData.Pages.FounderPages.FounderProfile.Save[langState]}</ShadcnButton>}
              />
                <Form
                  formDefinition={profileFormPersonalInfoDefinition}
                  formData={personalInfo}
                  primaryButtonProp={saveSectionButtonProp}
                  hideButtons
                  submitRef={personalInfoSubmitRef}
                  modifierFunc={personalFormModifier}
                  onFormDataChange={(data) => setPersonalInfo(data as T.PersonalInfo)}
                />
            </div>
          }
          {
            currentHTML === "companyInfo" &&
            <div className="flex-col flex">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.CompanyInfo[langState]}
                actions={<ShadcnButton onClick={() => companyInfoSaveRef.current?.()}>{textData.Pages.FounderPages.FounderProfile.Save[langState]}</ShadcnButton>}
              />
              <CompanyInfoSection
                founderProfile={founderProfile}
                mailingAddress={mailingAddress}
                onSave={handleCompanyInfoSave}
                onValidationFail={() => showToast({
                  toastType: "failure",
                  message: textData.Pages.FounderPages.FounderProfile.RequiedFields[langState]
                })}
                langState={langState}
                onSaveRef={companyInfoSaveRef}
              />
            </div>
          }
          {
            currentHTML === "pitchDeck" &&
            pitchDeckComplete === "incomplete" &&
            <div className="flex flex-col gap-6 w-full">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.Pitch[langState]}
                actions={<ShadcnButton onClick={pdfSave} disabled={!pdfUploaded}>{textData.Pages.FounderPages.FounderProfile.Save[langState]}</ShadcnButton>}
              />
              <div>
                <p className="text-muted-foreground">{textData.Pages.FounderPages.FounderProfile.PitchSub[langState]}</p>
              </div>
              <div className="flex flex-col gap-4">
                <p>{textData.Pages.FounderPages.FounderProfile.PitchUp[langState]}</p>
                <p>{textData.Pages.FounderPages.FounderProfile.DocUp[langState]}</p>
                <div className="flex flex-col gap-6">
                  <div>
                    <p className="font-semibold">{textData.Pages.FounderPages.FounderProfile.Req1Title[langState]}</p>
                    <ul className="ml-6 list-disc mt-1 text-sm text-muted-foreground">
                      <li>{textData.Pages.FounderPages.FounderProfile.Req1List1[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req1List2[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req1List3[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req1List4[langState]}</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold">{textData.Pages.FounderPages.FounderProfile.Req2Title[langState]}</p>
                    <ul className="ml-6 list-disc mt-1 text-sm text-muted-foreground">
                      <li>{textData.Pages.FounderPages.FounderProfile.Req2List1[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req2List2[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req2List3[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req2List4[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req2List5[langState]}</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold">{textData.Pages.FounderPages.FounderProfile.Req3Title[langState]}</p>
                    <ul className="ml-6 list-disc mt-1 text-sm text-muted-foreground">
                      <li>{textData.Pages.FounderPages.FounderProfile.Req3List1[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req3List2[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req3List3[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req3List4[langState]}</li>
                      <li>{textData.Pages.FounderPages.FounderProfile.Req3List5[langState]}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <Card>
                <CardContent className="pt-6">
                  <FileUpload handlePdfStateChange={handlePdfStateChange} />
                  {pdfUploaded && pitchDeckComplete === "incomplete" &&
                    <div className="flex items-center gap-2 mt-3">
                      <CheckCircle2 className="size-4 text-green-600" />
                      <p className="text-sm text-green-700">{textData.Pages.FounderPages.FounderProfile.NewSuccess[langState]}</p>
                    </div>
                  }
                </CardContent>
              </Card>
            </div>
          }
          {currentHTML === "pitchDeck" && pitchDeckComplete === "complete" &&
            <div className="w-full flex flex-col gap-6">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.Pitch[langState]}
                actions={<ShadcnButton onClick={() => setCurrentHTML("needHelpWith")}>{textData.Pages.FounderPages.FounderProfile.Next[langState]}</ShadcnButton>}
              />
              <Card className="max-w-lg">
                <CardContent className="flex flex-col items-center gap-4 pt-8 pb-6">
                  <div className="h-14 w-14 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="font-semibold">{textData.Pages.FounderPages.FounderProfile.PitchReceived[langState]}</h4>
                  <ShadcnButton variant="secondary" className="mt-2" onClick={() => setPitchDeckComplete("incomplete")}>
                    <Upload className="size-4 mr-1" />
                    {textData.Pages.FounderPages.FounderProfile.UploadNew[langState]}
                  </ShadcnButton>
                </CardContent>
              </Card>
            </div>
          }
          {
            currentHTML === "needHelpWith" &&
            <div className="flex-col flex">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.HelpWith[langState]}
                actions={<ShadcnButton onClick={() => needHelpWithSaveRef.current?.()}>{textData.Pages.FounderPages.FounderProfile.Save[langState]}</ShadcnButton>}
              />
              <NeedHelpWithSection
                founderProfile={founderProfile}
                regionalFirmOptions={regionalFirmOptions}
                onSave={handleNeedHelpWithSave}
                onValidationFail={() => showToast({
                  toastType: "failure",
                  message: textData.Pages.FounderPages.FounderProfile.RequiedFields[langState]
                })}
                langState={langState}
                onSaveRef={needHelpWithSaveRef}
              />
            </div>
          }          {
            currentHTML === "programingEvaluation" &&
            <div className="flex flex-col">
              <ApplicationSectionHeader
                title={textData.Pages.FounderPages.FounderProfile.ServiceEval[langState]}
                actions={<ShadcnButton onClick={() => programingEvalSubmitRef.current?.()}>{textData.Pages.FounderPages.FounderProfile.Save[langState]}</ShadcnButton>}
              />
              <Form
                formDefinition={profileFormProgramingEvaluationDefinition}
                formData={programingEvaluation}
                primaryButtonProp={saveSectionButtonProp}
                hideButtons
                submitRef={programingEvalSubmitRef}
                onFormDataChange={(data) => setProgramingEvaluation(data as T.ProgramingEvaluation)}
              />
            </div>
          }
          {
            currentHTML === "review" &&
            <div className="w-full flex flex-col">
              <ApplicationSectionHeader
                title={memberApplicationStatus === "pendingEditRequest"
                  ? textData.Pages.FounderPages.FounderProfile.RevEdit[langState]
                  : textData.Pages.FounderPages.FounderProfile.Rev[langState]}
                actions={
                  (() => {
                    const isDisabled = memberApplicationStatus === "pending" || memberApplicationStatus === "flagged" || memberApplicationStatus === "rejected" || memberApplicationStatus === "approved";
                    const canEdit = memberApplicationStatus === "unsubmitted" || memberApplicationStatus === "pendingEditRequest" || memberApplicationStatus === "rejectedReapply";
                    const submitButton = (
                      <ShadcnButton
                        onClick={modalProfileCheck}
                        disabled={isDisabled}
                      >
                        {textData.Pages.FounderPages.FounderProfile.SubmitApp[langState]}
                      </ShadcnButton>
                    );
                    return (
                      <div className="flex gap-2">
                        {canEdit && (
                          <ShadcnButton variant="secondary" onClick={() => setCurrentHTML("personalInfo")}>
                            <Pencil className="h-4 w-4 mr-1" />Edit
                          </ShadcnButton>
                        )}
                        {isDisabled ? (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0}>{submitButton}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {memberApplicationStatus === "rejected"
                                    ? "Your application was not approved."
                                    : memberApplicationStatus === "approved"
                                    ? "Your application has been approved."
                                    : "Your application has already been submitted and is being reviewed."}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : submitButton}
                      </div>
                    );
                  })()
                }
              />
              <p className="bodyLarger mb-8">
                {memberApplicationStatus === "pendingEditRequest"
                  ? textData.Pages.FounderPages.FounderProfile.RevSubEdit[langState]
                  : textData.Pages.FounderPages.FounderProfile.RevSub[langState]}
              </p>
              <div className="flex flex-col applicationFormWidth">
                <div className="flex items-center justify-between mb-6">
                  <h4>{textData.Pages.FounderPages.FounderProfile.YourInfo[langState]}</h4>
                  {(memberApplicationStatus === "unsubmitted" || memberApplicationStatus === "pendingEditRequest" || memberApplicationStatus === "rejectedReapply") && (
                    <ShadcnButton variant="ghost" size="sm" onClick={() => setCurrentHTML("personalInfo")}>
                      <Pencil className="h-4 w-4 mr-1" />Edit
                    </ShadcnButton>
                  )}
                </div>
                <Form
                  formDefinition={profileFormPersonalInfoDefinition}
                  formData={founderProfile?.personalInfo ?? null}
                  readOnly
                  modifierFunc={personalFormModifier}
                  disabled
                />
                <div className="flex items-center justify-between mb-6 mt-8">
                  <h4>{textData.Pages.FounderPages.FounderProfile.CompanyInfo[langState]}</h4>
                  {(memberApplicationStatus === "unsubmitted" || memberApplicationStatus === "pendingEditRequest" || memberApplicationStatus === "rejectedReapply") && (
                    <ShadcnButton variant="ghost" size="sm" onClick={() => setCurrentHTML("companyInfo")}>
                      <Pencil className="h-4 w-4 mr-1" />Edit
                    </ShadcnButton>
                  )}
                </div>
                {founderProfile &&
                  <Form
                    formDefinition={getModifiedCompanyInfoFormDef(founderProfile)}
                    formData={founderProfile.companyInfo ? companyInfoToForm(founderProfile.companyInfo) : null}
                    readOnly
                    disabled
                  />
                }
                <div className="flex items-center justify-between mb-6 mt-8">
                  <h4>{textData.Pages.FounderPages.FounderProfile.HelpWith[langState]}</h4>
                  {(memberApplicationStatus === "unsubmitted" || memberApplicationStatus === "pendingEditRequest" || memberApplicationStatus === "rejectedReapply") && (
                    <ShadcnButton variant="ghost" size="sm" onClick={() => setCurrentHTML("needHelpWith")}>
                      <Pencil className="h-4 w-4 mr-1" />Edit
                    </ShadcnButton>
                  )}
                </div>
                <Form
                  formDefinition={founderProfile ? getNeedHelpFormDef(founderProfile, regionalFirmOptions) : needHelpWithDefinition}
                  formData={founderProfile?.needHelpWith ?? null}
                  readOnly
                  disabled
                />
                <div className="flex items-center justify-between mb-6 mt-8">
                  <h4>{textData.Pages.FounderPages.FounderProfile.ServiceEval[langState]}</h4>
                  {(memberApplicationStatus === "unsubmitted" || memberApplicationStatus === "pendingEditRequest" || memberApplicationStatus === "rejectedReapply") && (
                    <ShadcnButton variant="ghost" size="sm" onClick={() => setCurrentHTML("programingEvaluation")}>
                      <Pencil className="h-4 w-4 mr-1" />Edit
                    </ShadcnButton>
                  )}
                </div>
                <Form
                  formDefinition={profileFormProgramingEvaluationDefinition}
                  formData={founderProfile?.programingEvaluation ?? null}
                  readOnly
                  disabled
                />
              </div>
            </div>
          }
          </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        modalHeader={""}
        contentLabel={textData.Pages.FounderPages.FounderProfile.AttestatioLabel[langState]}
        primaryButton={{ buttonText: textData.Pages.FounderPages.FounderProfile.Confirm[langState], buttonCallback: () => isAttestationChecked ? handleSubmitMembershipApplication() : attestationPrompt() }}
        modalHTML={modalHTML}
      />
    </div>
  )
}

export default FounderProfileUpdated;